import { Component, OnInit, ViewChild, ElementRef, Input, ViewEncapsulation, AfterViewInit } from '@angular/core';
import { KlaHttpService } from '../../tools/services/kla.http.service';
import { RingGraphConfigurations } from '../../tools/ring-graph/ring-graph-model';
import * as d3 from 'd3';
import { klaIcons } from "./kla-icons";
import { v4 as uuid } from 'uuid';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'kla-ring-chart',
  templateUrl: './ring-chart.component.html',
  styleUrls: ['./ring-chart.component.scss'],
  standalone: true,
  imports: [ CommonModule ]
})
export class RingChartComponent implements OnInit, AfterViewInit {
  @Input()
  public config: RingGraphConfigurations;

  @Input()
  public chartAltText: string;

  @ViewChild('ring', {static: true}) private chartContainer: ElementRef;

  svgChildId = uuid();
  svgId = uuid();

  constructor(private httpService: KlaHttpService) { }

  onContainerKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      const firstSegment = this.chartContainer.nativeElement.querySelector('[tabindex="0"]:not(div)');
      if (firstSegment) { firstSegment.focus(); }
    }
  }

  ngOnInit() {
    this.createChart();
  }

  ngAfterViewInit(): void {
    this.setSvgHeight();
  }

  @Input('width')
  set setWidth(width: number) {
    if (!this.config) { return; }
    this.config.width = width;
    this.ngOnInit();
  }

  createViewPort(width: number, height: number) {
    d3.select(this.chartContainer.nativeElement).selectAll(".single-donut-graph").remove();

    return d3.select(this.chartContainer.nativeElement)
      .append('svg')
      .attr('id', this.svgId)
      .attr('class', 'pie single-donut-graph')
      .attr('width', width)
      .append('g')
      .attr('id', this.svgChildId)
      .attr('transform', 'translate(' + (10 + (width / 2)) + ',' + (10 + (height / 2)) + ')')
  }
  createDonutWrap(view, data, radius, thickness) {
    let arcline = d3.arc<any>()
      .innerRadius(radius - thickness)
      .outerRadius(radius)
      .cornerRadius(20)
      .startAngle(0 - 0.08)
      .endAngle(data.endAngle);

    view.append("g")
      .append('path')
      .attr('d', arcline)
      .attr('fill', "#F2F4F5")
  }

  createDonut(view, data, radius, thickness) {
    var arcline = d3.arc<any>()
      .innerRadius(radius - 17)
      .outerRadius(radius - 12)
      .cornerRadius(20)
      .startAngle(0)
      .endAngle(data.endAngle);

    view.append("g")
      .append('path')
      .attr('d', arcline)
      .attr('fill', data.color)
  }

  createStartpoint(view, data, radius, thickness) {
    view.append("circle")
      .attr("ry", 0)
      .attr("cx", 0)
      .attr("cy", -Math.abs(radius) + (thickness / 2))
      .attr("r", 5)
      .attr("fill", data.color);
  }

  addText(view, data, radius, thickness) {
    let titleWidth;
    view.append("g").append("text")
      .attr("class", "description")
      .text(data.title)
      .attr('text-anchor', 'middle')
      .attr('dy', -Math.abs(radius) + (thickness / 2) + 3)
      .attr('dx', function (d) {
        titleWidth = d3.select(this).node().getComputedTextLength();
        return -Math.abs(titleWidth / 2) - 30
      })
      .append("title")
      .text(data.title);

    view.append("g").append("text")
      .attr('class', 'heading bold')
      .text(data.percentTitle)
      .attr('text-anchor', 'middle')
      .attr('dy', -Math.abs(radius) + (thickness / 2) + 3)
      .attr('dx', function (d) {
        let percentWidth = d3.select(this).node().getComputedTextLength();
        return -Math.abs(percentWidth / 2) - titleWidth - 40
      })
      .append('title')
      .text(data.percentTitle);
  }

  createChart() {
    const percentRadian = 0.062831853071796;
    let width = this.config.width;
    let height = this.config.height;
    let thickness = this.config.thickness;
    let radius = Math.min(width, height) / 2;

    let view = this.createViewPort(width, height);
    const itemGroups: any[] = [];

    const focusItem = (targetGroup) => {
      itemGroups.forEach(g => g.attr('tabindex', '-1').attr('opacity', 0.4));
      targetGroup.attr('tabindex', '0').attr('opacity', 1);
      targetGroup.node().focus();
    };

    this.config.data.forEach((item, index) => {
      radius = radius - (index != 0 ? 40 : 0);
      let data = {
        image: item.image || undefined,
        icon: item.icon || undefined,
        fontSize: item.fontSize || undefined,
        percent: item.percent,
        color: item.color,
        endAngle: item.percent * percentRadian,
        title: item.title,
        percentTitle: item.percent + '%'
      };

      const itemGroup = view.append('g')
        .attr('tabindex', index === 0 ? '0' : '-1')
        .attr('aria-label', `${data.title}: ${data.percentTitle}`)
        .attr('aria-roledescription', 'chart data')
        .style('cursor', 'pointer');

      itemGroups.push(itemGroup);

      itemGroup
        .on('mouseenter', () => {
          itemGroups.forEach(g => g.attr('opacity', 0.4));
          itemGroup.attr('opacity', 1);
        })
        .on('mouseleave', () => {
          itemGroups.forEach(g => g.attr('opacity', 1));
        })
        .on('focus', () => {
          itemGroups.forEach(g => g.attr('opacity', 0.4));
          itemGroup.attr('opacity', 1);
        })
        .on('blur', () => {
          itemGroups.forEach(g => g.attr('opacity', 1));
        })
        .on('keydown', function() {
          const e = d3.event;
          const currentIndex = itemGroups.indexOf(itemGroup);
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
            e.preventDefault();
            focusItem(itemGroups[(currentIndex + 1) % itemGroups.length]);
          } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            e.preventDefault();
            focusItem(itemGroups[(currentIndex - 1 + itemGroups.length) % itemGroups.length]);
          }
        });

      if (item.shadow) {
        this.createDonutWrap(itemGroup, data, radius, thickness);
      }
      this.createStartpoint(itemGroup, data, radius, thickness);
      data.percent = data.percent - (item.shadow ? 1 : 0);
      data.endAngle = data.percent * percentRadian;
      this.createDonut(itemGroup, data, radius, thickness);
      if (data.image) {
        this.addAndRotateImage(itemGroup, data, radius, thickness);
      }

      if (data.icon) {
        this.addIconToLine(itemGroup, data, radius, thickness);
      }

      this.addText(itemGroup, data, radius, thickness);
      let angleClone = data.endAngle;
      this.createDonut(itemGroup, data, radius, thickness);
      data.endAngle = angleClone;
      this.createStartpoint(itemGroup, data, radius, thickness);
    });
  }


  addIconToLine(view, data, radius, thickness) {
    const size = data.fontSize || 50;
    const fontSize = size + 'px';
    var icon = klaIcons[data.icon];
    var decimalCode = parseInt(icon.content, 16);

    view.append('svg:text')
      .attr('transform', 'rotate(' + (data.percent + 1) * (360 / 100) + ')')
      .attr('id', 'bg')
      .attr('x', 0)
      .attr('y', -Math.abs(radius) + (thickness / 2) + 5)
      .attr('font-size', fontSize)
      .style('font-family', 'fontello')
      .style('fill', data.color)
      .attr('width', size)
      .attr('height', size)
      .text(String.fromCharCode(decimalCode));
  }

  addAndRotateImage(view, data, radius, thickness) {
    const imagePath = this.httpService.getImagePath(data.image.id.toString(), data.image.width);
    view.append('image')
      .attr('transform', 'rotate(' + data.percent * (360 / 100) + ')')
      .attr('id', 'bg')
      .attr('xlink:href', imagePath)
      .attr('x', 0)
      .attr('y', -Math.abs(radius) - ((data.image.width - thickness) / 2))
      .attr('width', data.image.width)
      .attr('height', data.image.width);
  }

  setSvgHeight() {
    let svgHeight = document.getElementById(`${this.svgChildId}`).getBoundingClientRect().height;
    document.getElementById(`${this.svgId}`).setAttribute('height', (svgHeight + 50).toString())
  }

}
